import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaChartLine } from 'react-icons/fa';
import { Student } from '../types';

interface ParentResultsProps {
    students: Student[];
    onBack: () => void;
}

interface Term {
    id: number;
    name: string;
    is_active: boolean;
}

const ParentResults: React.FC<ParentResultsProps> = ({ students, onBack }) => {
    const [selectedStudentId, setSelectedStudentId] = useState<number>(students.length > 0 ? students[0].id : 0);
    const [terms, setTerms] = useState<Term[]>([]);
    const [selectedTermId, setSelectedTermId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Select first student by default
        if (students.length > 0 && selectedStudentId === 0) {
            setSelectedStudentId(students[0].id);
        }
    }, [students, selectedStudentId]);

    useEffect(() => {
        // Fetch Terms
        const fetchTerms = async () => {
            try {
                const res = await axios.get('/exams/terms/');
                const termsData = Array.isArray(res.data) ? res.data : [];
                setTerms(termsData);
                // Auto-select active term
                const active = termsData.find((t: Term) => t.is_active);
                if (active) setSelectedTermId(active.id);
                else if (termsData.length > 0) setSelectedTermId(termsData[0].id);
            } catch (err) {
                console.error("Failed to fetch terms", err);
            }
        };
        fetchTerms();
    }, []);

    useEffect(() => {
        if (!selectedStudentId || !selectedTermId) return;

        const fetchScores = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch raw scores for this student and term
                const res = await axios.get('/exams/assessment-scores/', {
                    params: {
                        student_id: selectedStudentId,
                        term_id: selectedTermId
                    }
                });

                // Structure Data: Subject -> { cat, final, total, grade }
                // Note: Backend returns list of SubjectAssessmentScore objects
                // We need to pivot them by subject

                const rawScores = res.data.results || res.data || [];
                const processed: any = {};

                rawScores.forEach((score: any) => {
                    const subName = score.subject_name;
                    const subId = score.subject;

                    if (!processed[subId]) {
                        processed[subId] = {
                            id: subId,
                            name: subName,
                            cat: 0,
                            final: 0,
                            total: 0
                        };
                    }

                    const val = parseFloat(score.score);
                    const type = score.assessment_type_code;

                    if (type === 'CAT') processed[subId].cat += val;
                    else if (type === 'FINAL') processed[subId].final += val;

                    processed[subId].total += val;
                });

                setResults(Object.values(processed));

            } catch (err) {
                console.error(err);
                setError("Waa la waayay natiijooyinka.");
            } finally {
                setLoading(false);
            }
        };

        fetchScores();
    }, [selectedStudentId, selectedTermId]);

    const currentStudent = students.find(s => s.id === selectedStudentId);

    return (
        <div className="p-3">
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
                <button onClick={onBack} className="btn btn-light rounded-circle me-3 shadow-sm">
                    <i className="fas fa-arrow-left"></i>
                </button>
                <div>
                    <h4 className="fw-bold mb-0">Natiijooyinka Tacliinta</h4>
                    <p className="text-muted small mb-0">Arag waxqabadka {currentStudent?.name}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-12">
                            <label className="form-label fw-bold text-secondary small text-uppercase">Ardayga</label>
                            <select
                                className="form-select bg-light border-0"
                                value={selectedStudentId}
                                onChange={(e) => setSelectedStudentId(Number(e.target.value))}
                            >
                                {students.map(s => (
                                    <option key={s.id} value={s.id}>{s.name} - {s.class_name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-bold text-secondary small text-uppercase">Simistar</label>
                            <select
                                className="form-select bg-light border-0"
                                value={selectedTermId || ''}
                                onChange={(e) => setSelectedTermId(Number(e.target.value))}
                            >
                                {terms.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} {t.is_active ? '(Jira)' : ''}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Results List */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2 text-muted">Natiijooyinka soo kabanaya...</p>
                </div>
            ) : error ? (
                <div className="alert alert-danger text-center">{error}</div>
            ) : results.length > 0 ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white py-3">
                        <h6 className="fw-bold mb-0 text-primary">
                            <FaChartLine className="me-2" />
                            Warbixinta Natiijada
                        </h6>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-striped align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-3 text-secondary text-uppercase small">Maaddo</th>
                                    <th className="text-center text-secondary text-uppercase small">CAT</th>
                                    <th className="text-center text-secondary text-uppercase small">Final</th>
                                    <th className="text-center text-secondary text-uppercase small">Wadarta</th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((sub: any) => (
                                    <tr key={sub.id}>
                                        <td className="ps-3 fw-bold text-dark">{sub.name}</td>
                                        <td className="text-center text-muted">{sub.cat}</td>
                                        <td className="text-center text-muted">{sub.final}</td>
                                        <td className="text-center fw-bold text-primary">{sub.total.toFixed(0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-light fw-bold">
                                <tr>
                                    <td className="ps-3">Wadarta Guud</td>
                                    <td colSpan={2}></td>
                                    <td className="text-center text-primary fs-6">
                                        {results.reduce((acc, curr) => acc + curr.total, 0).toFixed(0)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="text-center py-5">
                    <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                        <FaChartLine className="text-secondary opacity-50" size={32} />
                    </div>
                    <h6 className="fw-bold text-dark">Natiijooyin Lama Helin</h6>
                    <p className="text-muted small">Ma jiraan dhibco la diiwaangeliyay xilligan.</p>
                </div>
            )}

        </div>
    );
};

export default ParentResults;
